import { FC, useCallback, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  IoClose,
  IoDocumentTextOutline,
  IoImagesOutline,
  IoVideocamOutline,
} from "react-icons/io5";
import { BsPaperclip } from "react-icons/bs";
import toast from "react-hot-toast";
import heic2any from "heic2any";
import { LuLoader } from "react-icons/lu";
import classNames from "classnames";
import { BYTES_PER_MB, DEFAULT_MAX_FILES } from "@/constants";
import { FileAnswerType, TemporaryFile } from "@/types";
import {
  DROPZONE_ACCEPT,
  DROPZONE_IMAGE_EXTENSIONS,
  DROPZONE_PDF_IMAGE_EXTENSIONS,
  DROPZONE_VIDEO_EXTENSIONS,
  type DropzoneUploaderKind,
} from "@/utils/attachmentAccept";
import { getMaxFilesExceededToastMessage } from "@/pages/quiz/base/common";

interface DropzoneUploaderProps {
  onChange: (value: File[]) => void;
  value: File[] | undefined;
  type: DropzoneUploaderKind;
  maxFileSizeMB: number;
  maxFiles: number;
  temporaryFiles?: TemporaryFile[];
  answerType?: FileAnswerType;
}

const placeholders: Record<DropzoneUploaderKind, string> = {
  video: "Choose a video from gallery",
  image: "Choose an image from gallery",
  pdf_image: "Upload PDF or images",
};

const icons: Record<DropzoneUploaderKind, JSX.Element> = {
  video: <IoVideocamOutline className="icon-size" />,
  image: <IoImagesOutline className="icon-size" />,
  pdf_image: <IoDocumentTextOutline className="icon-size" />,
};

function isPdfPath(path: string): boolean {
  return /\.pdf(\?|$)/i.test(path);
}

const NewDropzoneUploader: FC<DropzoneUploaderProps> = ({
  value = [],
  type,
  maxFiles,
  maxFileSizeMB,
  onChange,
  temporaryFiles = [],
  answerType,
}) => {
  const [loading, setLoading] = useState(false);

  /* DESC: value can be a string "true" if it's from server */
  /* @ts-ignore */
  const updatedValue = value === "true" ? [] : value;

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections[0]) {
        const errorMessage =
          fileRejections[0].errors?.[0]?.message || "Some error";
        toast.error(errorMessage);
        return;
      }

      setLoading(true);
      const convertedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const ext = file.name.split(".").pop()?.toLowerCase();

          if (ext === "heic" || ext === "heif") {
            // Convert HEIC/HEIF file to JPEG
            try {
              const outputBlob = (await heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.8,
              })) as Blob;

              // Replace the source file with a new Blob with ".jpg" extension
              return new File(
                [outputBlob],
                file.name.replace(/\.(heic|heif)$/i, ".jpg"),
                {
                  type: "image/jpeg",
                  lastModified: file.lastModified,
                }
              );
            } catch (error) {
              console.error("Error converting image:", error);
              return file;
            }
          } else {
            return file;
          }
        })
      );

      const newFiles = convertedFiles.filter((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (!ext) {
          toast.error(`${file.name} has invalid format`);
          return false;
        }

        if (type === "video") {
          if (
            !file.type.startsWith("video/") &&
            !DROPZONE_VIDEO_EXTENSIONS.includes(ext)
          ) {
            toast.error(`${file.name} has invalid format`);
            return false;
          }
        } else if (type === "pdf_image") {
          if (!DROPZONE_PDF_IMAGE_EXTENSIONS.includes(ext)) {
            toast.error(`${file.name} has invalid format`);
            return false;
          }
        } else if (!DROPZONE_IMAGE_EXTENSIONS.includes(ext)) {
          toast.error(`${file.name} has invalid format`);
          return false;
        }

        const fileSizeMB = file.size / BYTES_PER_MB;
        if (fileSizeMB > maxFileSizeMB) {
          toast.error(
            `${file.name} exceeds the size limit of ${maxFileSizeMB} MB.`
          );
          return false;
        }
        return true;
      });

      if (updatedValue.length + newFiles.length > maxFiles) {
        const toastMessage = getMaxFilesExceededToastMessage({
          maxFiles,
          answerType,
          maxFileSizeMB,
        });

        toast.error(toastMessage);
      } else {
        onChange([...updatedValue, ...newFiles]);
      }
      setLoading(false);
    },
    [updatedValue, maxFiles, maxFileSizeMB, onChange, type, answerType]
  );

  const handleRemoveFile = (fileToRemove: File) => {
    const updatedFiles = updatedValue.filter((file) => file !== fileToRemove);
    onChange(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT[type],
  });

  return (
    <div className="flex flex-col gap-4">
      <div
        className={classNames(
          "h-20 sm:h-28 lg:h-36 w-full rounded-md flex items-center",
          {
            "border border-dashed border-silver": loading,
          }
        )}
      >
        {loading ? (
          <LuLoader className="w-7 h-7 mx-auto text-blue animate-spin" />
        ) : (
          <div {...getRootProps({ className: "dropzone dropzone-area" })}>
            <input {...getInputProps()} />
            <div className="text-silver">
              {maxFiles > DEFAULT_MAX_FILES ? (
                <BsPaperclip className="icon-size" />
              ) : (
                icons[type]
              )}
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-silver text-center">
              {maxFiles > DEFAULT_MAX_FILES
                ? "Select multiple files or drag and drop them here"
                : placeholders[type]}
            </p>
          </div>
        )}
      </div>

      <div>
        <ul className="flex flex-col gap-3">
          {updatedValue.length
            ? updatedValue.map((file, i) => (
                <li
                  key={i}
                  className="flex gap-2 items-center rounded-full bg-light-gray-2 px-3 py-1 max-w-56 w-fit"
                >
                  <div className="text-base text-gray-dark-2 leading-none truncate">
                    {file.name}
                  </div>
                  <IoClose
                    color="gray-dark-2"
                    className="cursor-pointer w-4 h-4 min-w-4 min-h-4"
                    onClick={() => handleRemoveFile(file)}
                  />
                </li>
              ))
            : Boolean(temporaryFiles.length) &&
              temporaryFiles.map((file, i) => (
                <li
                  key={i}
                  className="flex gap-2 items-center rounded-full bg-light-gray-2 px-3 py-1 max-w-56 w-fit"
                >
                  {isPdfPath(file.path) ? (
                    <IoDocumentTextOutline className="w-6 h-6 shrink-0 text-gray-dark-2" />
                  ) : (
                    <img
                      className="w-6 h-6"
                      src={file.path}
                      alt={`photo-${i}`}
                    />
                  )}
                </li>
              ))}
        </ul>
      </div>
    </div>
  );
};

export default NewDropzoneUploader;
